import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import type { ViewProps } from "../views/OverviewView";
import type { TablePayload } from "../types";
import { useWebsiteReport } from "../useAnalyticsContext";
import { rangeParams } from "../dateRange";
import { cellValue } from "../format";
import { ErrorState, Loading } from "./States";
import styles from "../Analytics.module.css";
export default function PageChanges({ scope, id, range, context }: ViewProps) {
  const search = context.sources.gsc.connection_status !== "not_connected";
  const comparable = search && range.compare === "previous";
  const first = useWebsiteReport<TablePayload>(
    scope,
    id,
    range,
    context,
    "pages",
    {
      page: 1,
      page_size: 5,
      sort: comparable ? "-click_change" : search ? "-clicks" : "-sessions",
    },
  );
  const second = useWebsiteReport<TablePayload>(
    scope,
    id,
    range,
    context,
    "pages",
    { page: 1, page_size: 5, sort: "click_change" },
    comparable,
  );
  const link = `/analytics/websites/${id}/pages?${rangeParams(range)}`;
  if (first.isPending || (comparable && second.isPending))
    return <Loading label="Loading page changes…" />;
  if (first.isError)
    return <ErrorState error={first.error} retry={() => first.refetch()} />;
  const rising =
    first.data?.payload.items.filter(
      (row) => typeof row.click_change === "number" && row.click_change > 0,
    ) ?? [];
  const falling =
    second.data?.payload.items.filter(
      (row) => typeof row.click_change === "number" && row.click_change < 0,
    ) ?? [];
  const groups = comparable
    ? [
        { title: "Gaining search clicks", rows: rising, up: true },
        { title: "Declining search clicks", rows: falling, up: false },
      ]
    : [
        {
          title: "Pages to inspect",
          rows: first.data?.payload.items ?? [],
          up: true,
        },
      ];
  return (
    <section className={styles.panel}>
      <div className={styles.panelHeading}>
        <div>
          <h2>Page performance</h2>
          <p>
            {comparable
              ? "Changes with comparable Search Console history"
              : "Pages with available activity in this period"}
          </p>
        </div>
        <Link to={link} className={styles.textLink}>
          View all pages →
        </Link>
      </div>
      {comparable && !rising.length && !falling.length ? (
        <p className={styles.insightEmpty}>
          No comparable page changes are available. Review page performance for
          the available source results and coverage.
        </p>
      ) : (
        <div className={styles.pageChanges}>
          {groups.map((group) => (
            <article key={group.title}>
              <h3>
                {comparable &&
                  (group.up ? (
                    <ArrowUpRight size={15} />
                  ) : (
                    <ArrowDownRight size={15} />
                  ))}
                {group.title}
              </h3>
              {!group.rows.length ? (
                <p className={styles.caption}>
                  No {group.up ? "gaining" : "declining"} pages in the available
                  results.
                </p>
              ) : (
                <ul>
                  {group.rows.map((row) => (
                    <li key={String(row.page_key)}>
                      <Link
                        to={`${link}&pageKey=${encodeURIComponent(String(row.page_key))}`}
                      >
                        {String(row.page)}
                      </Link>
                      <span>
                        {cellValue(
                          comparable
                            ? row.click_change
                            : search
                              ? row.clicks
                              : row.sessions,
                          comparable
                            ? "click_change"
                            : search
                              ? "clicks"
                              : "sessions",
                        )}
                        {!comparable && (
                          <small>{search ? "clicks" : "sessions"}</small>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      )}
      {second.isError && (
        <p className={styles.caption}>
          Declining pages could not be loaded.{" "}
          <button className={styles.textLink} onClick={() => second.refetch()}>
            Try again
          </button>
        </p>
      )}
    </section>
  );
}
